import type { JobCardExpense, JobCardPayload } from '../types/jobCard';
import type { JobCardCustomLine, JobCardDirectCostLabelKey } from '../schemas/jobCardFields';
import {
  JOB_CARD_DIRECT_COST_LABELS,
  JOB_CARD_EXPENSE_CAPACITY,
  JOB_CARD_LABOR_LABELS,
  JOB_CARD_REIMBURSEMENT_LABELS,
  JOB_CARD_WORKER_CAPACITY,
  JOB_CARD_WORKER_ROW_NAMES,
  JOB_CARD_WORKER_ROW_RATES,
  JOB_CARD_WORK_COMP_RATE,
} from '../constants/job-card-template';

export interface JobCardCostRow {
  label: string;
  rate: number | null;
  quantity: number | null;
  quantityUnit: string | null;
  cost: number;
}

export interface JobCardTotals {
  laborRows: JobCardCostRow[];
  laborTotal: number;
  reimbursementRows: JobCardCostRow[];
  reimbursementTotal: number;
  workerRows: JobCardCostRow[];
  workerOverflow: { name: string; hours: number }[];
  workerSubtotal: number;
  workComp: number;
  workerTotal: number;
  inlineExpenses: JobCardExpense[];
  expensesOverflowCount: number;
  expensesTotal: number;
  grandTotal: number;
  breakdownTotal: number;
}

/**
 * Excel rounds to cents on display; do the same so the preview reads identically.
 * Always round at the point of display, never mid-sum — Excel sums the unrounded
 * cell values, and rounding each addend first would drift from the workbook.
 */
const money = (n: number) => Math.round(n * 100) / 100;

/**
 * Rows 24/25 are the spare reimbursement rows the custom lines land on —
 * already inside G26's SUM range.
 */
const customRows = (lines: JobCardCustomLine[] | undefined): JobCardCostRow[] =>
  (lines ?? []).map((line) => ({
    label: line.label,
    rate: line.rate,
    quantity: line.qty,
    quantityUnit: null,
    cost: money(line.rate * line.qty),
  }));

/**
 * Reproduce, in TypeScript, the totals the JOB CARD sheet computes as formulas.
 *
 * The generated workbook ships without cached formula results (the Python script
 * calls `force_full_calc_on_load` and strips calcChain.xml), so these numbers do
 * not exist anywhere until Excel opens the file. Cell references below map to
 * `backend/templates/SAMPLE JOB CARD V6_1.xlsx`, sheet "JOB CARD".
 */
export function computeJobCardTotals(payload: JobCardPayload): JobCardTotals {
  const { labor, reimbursement, directCosts } = payload.jobCardFields;
  const sum = (rows: JobCardCostRow[]) => rows.reduce((acc, r) => acc + r.cost, 0);

  // Rows 10-12: G = D * F.
  const laborRows: JobCardCostRow[] = (
    [
      ['management', labor.management],
      ['office', labor.office],
      ['assistant', labor.assistant],
    ] as const
  ).map(([key, line]) => ({
    label: JOB_CARD_LABOR_LABELS[key],
    rate: line.rate,
    quantity: line.hours,
    quantityUnit: 'hr',
    cost: money(line.rate * line.hours),
  }));
  const laborTotal = money(sum(laborRows)); // G15

  // Absent key = keep the template's own label; present-but-empty = blank row.
  // Only the four flat-cost rows are renameable — toll and mileage are the
  // section's rate x qty rows and always read as the template names them.
  const labelFor = (key: JobCardDirectCostLabelKey, fallback: string) =>
    payload.jobCardFields.directCostLabels?.[key] ?? fallback;

  // Rows 18-19 are rate x qty; rows 20-23 are flat amounts typed straight into G.
  // Rows 24/25 are the spare custom rows, already inside G26.
  const reimbursementRows: JobCardCostRow[] = [
    ...(
      [
        ['toll', reimbursement.toll],
        ['mileage', reimbursement.mileage],
      ] as const
    ).map(([key, line]) => ({
      label: JOB_CARD_REIMBURSEMENT_LABELS[key],
      rate: line.rate,
      quantity: line.qty,
      quantityUnit: key === 'mileage' ? 'mi' : null,
      cost: money(line.rate * line.qty),
    })),
    ...(
      ['parking', 'gasEstimate', 'materials', 'homeDepot'] as const
    ).map((key) => ({
      label: labelFor(key, JOB_CARD_DIRECT_COST_LABELS[key]),
      rate: null,
      quantity: null,
      quantityUnit: null,
      cost: money(directCosts[key]),
    })),
    ...customRows(reimbursement.custom),
  ];
  const reimbursementTotal = money(sum(reimbursementRows)); // G26

  // Rows 29-34. Each placed worker is priced at their OWN rate (workers.rate),
  // which populate_worker_hours writes into column D. A worker with no rate set
  // leaves D blank, so the row shows no rate and costs nothing — deliberately
  // not 0, so an unset rate reads as unconfigured rather than as free labor.
  // With no hours at all the script writes nothing, so the sheet keeps its own
  // names and the per-row rates baked into the template; hence the two branches.
  const placed = payload.workerHours.slice(0, JOB_CARD_WORKER_CAPACITY);
  const workerOverflow = payload.workerHours.slice(JOB_CARD_WORKER_CAPACITY);
  const workerRows: JobCardCostRow[] =
    placed.length === 0
      ? JOB_CARD_WORKER_ROW_NAMES.map((name, i) => ({
          label: name,
          rate: JOB_CARD_WORKER_ROW_RATES[i] ?? 0,
          quantity: null,
          quantityUnit: 'hr',
          cost: 0,
        }))
      : placed.map((w) => ({
          label: w.name,
          rate: w.rate,
          quantity: w.hours,
          quantityUnit: 'hr',
          cost: money((w.rate ?? 0) * w.hours),
        }));
  const workerSubtotal = money(sum(workerRows));
  const workComp = money(workerSubtotal * JOB_CARD_WORK_COMP_RATE); // G36
  const workerTotal = money(workerSubtotal + workComp); // G37

  // Rows 54-70. Past capacity the sheet shows the first 16 plus a marker row, but
  // G71 points at the overflow sheet's total, so every expense still counts.
  const overflowing = payload.expenses.length > JOB_CARD_EXPENSE_CAPACITY;
  const inlineExpenses = overflowing
    ? payload.expenses.slice(0, JOB_CARD_EXPENSE_CAPACITY - 1)
    : payload.expenses;
  const expensesOverflowCount = overflowing
    ? payload.expenses.length - (JOB_CARD_EXPENSE_CAPACITY - 1)
    : 0;
  const expensesTotal = money(
    payload.expenses.reduce((acc, e) => acc + (e.cost || 0), 0)
  ); // G71

  return {
    laborRows,
    laborTotal,
    reimbursementRows,
    reimbursementTotal,
    workerRows,
    workerOverflow,
    workerSubtotal,
    workComp,
    workerTotal,
    inlineExpenses,
    expensesOverflowCount,
    expensesTotal,
    // G43 = G15+G26+G37+G71+G101+MISC!G56; the last two are 0 in the template and
    // are never written by the generator.
    grandTotal: money(laborTotal + reimbursementTotal + workerTotal + expensesTotal),
    // K46 = SUM(BREAKDOWN!C<total row>), which the script writes from summary.grandTotal.
    breakdownTotal: payload.summary.grandTotal,
  };
}
